import type { ReactNode } from 'react'
import { cn } from '../lib/cn.js'

/**
 * Заголовок экрана: название, пояснение и действия справа.
 *
 * Заголовок — та строка, которую человек ищет глазами, вернувшись к вкладке
 * через час, поэтому он заметно крупнее всего остального на экране.
 * Отличается размером и насыщенностью: отдельной гарнитуры для заголовков
 * в интерфейсе нет.
 */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string
  description?: ReactNode
  actions?: ReactNode
}) {
  return (
    <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-[22px] leading-tight font-semibold tracking-[-0.015em] text-ink">
          {title}
        </h1>
        {description && <p className="mt-1 max-w-2xl text-[13px] text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  )
}

/** Блок с рамкой. Группирует связанное — форму, панель фильтров, сводку. */
export function Panel({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: string
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('rounded-lg border border-line bg-raised', className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line-soft px-4 py-3">
          <div>
            {title && <h2 className="text-sm font-semibold text-ink">{title}</h2>}
            {description && <p className="mt-0.5 text-xs text-muted">{description}</p>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className="p-4">{children}</div>
    </section>
  )
}

/** Пара «подпись — значение» для сводок. */
export function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: ReactNode
  tone?: 'neutral' | 'warn' | 'bad'
}) {
  return (
    <div className="rounded-lg border border-line bg-raised px-3.5 py-3">
      <p className="label-xs">{label}</p>
      <p
        className={cn(
          'mt-1 text-xl font-semibold tabular-nums',
          tone === 'warn' && 'text-copper',
          tone === 'bad' && 'text-crimson',
          (!tone || tone === 'neutral') && 'text-ink',
        )}
      >
        {value}
      </p>
    </div>
  )
}
